/*
 * Small, dependency-free client event queue.
 *
 * The queue deliberately knows nothing about the business API wrapper.  The
 * sender is injected by the Mini App and performs a direct POST to the batch
 * endpoint, so an event-capture outage cannot recurse through api() or block a
 * mutation.
 */

export const EVENT_BATCH_SIZE = 100;
export const EVENT_MAX_BYTES = 8 * 1024;
export const OUTBOX_MAX_EVENTS = 5_000;
export const OUTBOX_MAX_BYTES = 5 * 1024 * 1024;
export const CLIENT_EVENT_NAME = {
  APP_OPENED: "ui.app_opened",
  APP_CLOSED: "ui.app_closed",
  APP_VISIBILITY_CHANGED: "ui.app_visibility_changed",
  BOTTOM_NAV_TAPPED: "ui.bottom_nav_tapped",
  CAMERA_PERMISSION_REQUESTED: "ui.camera_permission_requested",
  CAMERA_PERMISSION_RESULT: "ui.camera_permission_result",
  CLIENT_ERROR: "ui.client_error",
  CTA_TAPPED: "ui.cta_tapped",
  ERROR_SHOWN: "ui.error_shown",
  EVENT_OUTBOX_FALLBACK: "ui.event_outbox_fallback",
  EVENTS_DROPPED: "events.dropped",
  GAME_SESSION: "ui.game_session",
  GESTURE_COMPLETED: "ui.gesture_completed",
  INPUT_CHANGED: "ui.input_changed",
  LANGUAGE_CHANGED: "ui.language_changed",
  NETWORK_CHANGED: "ui.network_changed",
  PROFILE_SAVED: "ui.profile_saved",
  QR_DETECTED: "ui.qr_detected",
  SCREEN_VIEWED: "ui.screen_viewed",
  SHEET_CLOSED: "ui.sheet_closed",
  SHEET_OPENED: "ui.sheet_opened",
  TEAM_SAVED: "ui.team_saved",
} as const;
export const EVENT_RESULT = {
  RECEIVED: "received",
  STARTED: "started",
  SUCCEEDED: "succeeded",
  ACCEPTED: "accepted",
  REJECTED: "rejected",
  FAILED: "failed",
  SKIPPED: "skipped",
} as const;
export type ClientEventName = (typeof CLIENT_EVENT_NAME)[keyof typeof CLIENT_EVENT_NAME];
export type EventResultValue = (typeof EVENT_RESULT)[keyof typeof EVENT_RESULT];
const DB_VERSION = 1;
const DEFAULT_DB_NAME = "sting-miniapp-event-outbox";
const STORE_NAME = "events";
const MAX_RETRY_DELAY_MS = 60_000;
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const PLACEHOLDER_EVENT_ID = "00000000-0000-4000-8000-000000000000";
const VALID_RESULTS = new Set(Object.values(EVENT_RESULT));
const FORBIDDEN_PAYLOAD_KEYS = new Set([
  "accountnumber",
  "authorization",
  "bankaccount",
  "cardnumber",
  "clientip",
  "cookie",
  "cvv",
  "initdata",
  "ip",
  "kiz",
  "pan",
  "pinfl",
  "rawinitdata",
  "rawip",
  "rawkiz",
  "rawuseragent",
  "servicesecret",
  "setcookie",
  "useragent",
]);

export type ClientEvent = {
  clientEventId: string;
  occurredAt: string;
  eventName: ClientEventName;
  result: EventResultValue;
  screen?: string;
  entityType?: string;
  entityId?: string;
  payload?: Record<string, unknown>;
};

export type EventInput = Omit<ClientEvent, "clientEventId" | "occurredAt"> &
  Partial<Pick<ClientEvent, "clientEventId" | "occurredAt">>;

export type EventBatchResponse = {
  accepted?: string[];
  duplicates?: string[];
};

export type EventSender = (events: ClientEvent[]) => Promise<EventBatchResponse>;

type StoredEvent = {
  clientEventId: string;
  event: ClientEvent;
  bytes: number;
  sequence: number;
};

type DropSummary = {
  count: number;
  bytes: number;
  oldestOccurredAt: string | null;
  newestOccurredAt: string | null;
};

type Store = {
  get(clientEventId: string): Promise<StoredEvent | undefined>;
  list(): Promise<StoredEvent[]>;
  replace(records: StoredEvent[]): Promise<void>;
  deleteMany(clientEventIds: string[]): Promise<void>;
};

type EventOutboxOptions = {
  sender?: EventSender;
  dbName?: string;
  forceMemory?: boolean;
  maxEvents?: number;
  maxBytes?: number;
  now?: () => Date;
};

function byteLength(value: unknown): number {
  let serialized = "";
  try {
    serialized = JSON.stringify(value) || "";
  } catch {
    return Number.POSITIVE_INFINITY;
  }
  if (typeof TextEncoder !== "undefined") return new TextEncoder().encode(serialized).byteLength;
  // TextEncoder is present in every supported browser.  This fallback keeps
  // the pure Node contract usable on older runtimes as well.
  return unescape(encodeURIComponent(serialized)).length;
}

export function serializedEventBytes(event: ClientEvent): number {
  return byteLength(event);
}

export function createClientEventId(): string {
  const randomUUID = globalThis.crypto && typeof globalThis.crypto.randomUUID === "function"
    ? globalThis.crypto.randomUUID.bind(globalThis.crypto)
    : null;
  if (randomUUID) return randomUUID();
  const random = () => Math.floor(Math.random() * 0x1_0000_0000).toString(16).padStart(8, "0");
  return `${random()}-${random().slice(0, 4)}-4${random().slice(0, 3)}-8${random().slice(0, 3)}-${random()}${random().slice(0, 4)}`;
}

function isoNow(now: () => Date): string {
  const value = now();
  return Number.isNaN(value.getTime()) ? new Date().toISOString() : value.toISOString();
}

function normalizedOccurredAt(value: unknown, now: () => Date): string {
  if (typeof value !== "string" || !value) return isoNow(now);
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? isoNow(now) : parsed.toISOString();
}

function eventNameAllowed(eventName: string): boolean {
  return eventName.length <= 96 && /^(?:ui\.[a-z0-9]+(?:[._-][a-z0-9]+)*|events\.dropped)$/.test(eventName);
}

function normalizedPayloadKey(key: string): string {
  return key.toLowerCase().replace(/[^a-z0-9]/g, "");
}

export function eventPayloadKeyIsForbidden(key: string): boolean {
  const normalized = normalizedPayloadKey(key);
  return FORBIDDEN_PAYLOAD_KEYS.has(normalized)
    || normalized.startsWith("authorization")
    || normalized.endsWith("initdata")
    || normalized.endsWith("servicesecret")
    || normalized.endsWith("token");
}

export function eventPayloadAllowed(value: unknown): boolean {
  if (Array.isArray(value)) return value.every(eventPayloadAllowed);
  if (!value || typeof value !== "object") return true;
  return Object.entries(value).every(([key, nested]) => {
    return !eventPayloadKeyIsForbidden(key) && eventPayloadAllowed(nested);
  });
}

function normalizeEvent(input: EventInput, now: () => Date): ClientEvent | null {
  if (!input || typeof input.eventName !== "string" || !eventNameAllowed(input.eventName)) return null;
  if (typeof input.result !== "string" || !VALID_RESULTS.has(input.result)) return null;
  if (input.clientEventId !== undefined && !UUID_PATTERN.test(input.clientEventId)) return null;
  const event: ClientEvent = {
    clientEventId: typeof input.clientEventId === "string" && input.clientEventId ? input.clientEventId : createClientEventId(),
    occurredAt: normalizedOccurredAt(input.occurredAt, now),
    eventName: input.eventName,
    result: input.result,
  };
  if (typeof input.screen === "string" && input.screen) event.screen = input.screen.slice(0, 64);
  if (typeof input.entityType === "string" && input.entityType) event.entityType = input.entityType.slice(0, 64);
  if (typeof input.entityId === "string" && input.entityId) event.entityId = input.entityId.slice(0, 128);
  if (input.payload !== undefined) {
    if (!input.payload || typeof input.payload !== "object" || Array.isArray(input.payload)) return null;
    try {
      // JSON serialization is also the backend's structured payload boundary.
      if (!eventPayloadAllowed(input.payload)) return null;
      JSON.stringify(input.payload);
      event.payload = input.payload;
    } catch {
      return null;
    }
  }
  return serializedEventBytes(event) <= EVENT_MAX_BYTES ? event : null;
}

/**
 * Check the size of the event after the same generated fields that
 * normalizeEvent adds.  This is deliberately narrower than normalizeEvent's
 * validation: malformed/forbidden events are rejected silently, while a
 * well-formed event that cannot fit the wire contract is accounted for.
 */
function eventInputIsOversized(input: EventInput, now: () => Date): boolean {
  if (!input || typeof input.eventName !== "string" || !eventNameAllowed(input.eventName)) return false;
  if (typeof input.result !== "string" || !VALID_RESULTS.has(input.result)) return false;
  if (input.clientEventId !== undefined && !UUID_PATTERN.test(input.clientEventId)) return false;
  if (input.payload !== undefined) {
    if (!input.payload || typeof input.payload !== "object" || Array.isArray(input.payload)) return false;
    try {
      if (!eventPayloadAllowed(input.payload)) return false;
    } catch {
      return false;
    }
  }
  let candidate: Record<string, unknown>;
  try {
    candidate = { ...input };
    candidate.clientEventId = typeof input.clientEventId === "string" && input.clientEventId
      ? input.clientEventId
      : PLACEHOLDER_EVENT_ID;
    candidate.occurredAt = normalizedOccurredAt(input.occurredAt, now);
  } catch {
    return false;
  }
  return byteLength(candidate) > EVENT_MAX_BYTES;
}

function boundedDropBytes(value: number): number {
  if (!Number.isFinite(value) || value < 0) return EVENT_MAX_BYTES;
  return Math.min(Number.MAX_SAFE_INTEGER, Math.max(0, Math.floor(value)));
}

function emptyDropSummary(): DropSummary {
  return { count: 0, bytes: 0, oldestOccurredAt: null, newestOccurredAt: null };
}

function earlierTimestamp(left: string | null, right: string | null): string | null {
  if (!left) return right;
  if (!right) return left;
  return left < right ? left : right;
}

function laterTimestamp(left: string | null, right: string | null): string | null {
  if (!left) return right;
  if (!right) return left;
  return left > right ? left : right;
}

function mergeDropSummary(target: DropSummary, source: DropSummary): void {
  target.count = Math.min(Number.MAX_SAFE_INTEGER, target.count + source.count);
  target.bytes = Math.min(Number.MAX_SAFE_INTEGER, target.bytes + source.bytes);
  target.oldestOccurredAt = earlierTimestamp(target.oldestOccurredAt, source.oldestOccurredAt);
  target.newestOccurredAt = laterTimestamp(target.newestOccurredAt, source.newestOccurredAt);
}

function dropSummaryFromMarker(record: StoredEvent): DropSummary | null {
  if (record.event.eventName !== CLIENT_EVENT_NAME.EVENTS_DROPPED) return null;
  const payload = record.event.payload;
  const count = payload?.droppedCount;
  const bytes = payload?.droppedBytes;
  const oldestOccurredAt = payload?.oldestOccurredAt;
  const newestOccurredAt = payload?.newestOccurredAt;
  if (!Number.isSafeInteger(count) || Number(count) < 1) return null;
  if (!Number.isSafeInteger(bytes) || Number(bytes) < 0) return null;
  if (oldestOccurredAt !== null && typeof oldestOccurredAt !== "string") return null;
  if (newestOccurredAt !== null && typeof newestOccurredAt !== "string") return null;
  return {
    count: Number(count),
    bytes: Number(bytes),
    oldestOccurredAt,
    newestOccurredAt,
  };
}

function addDrop(summary: DropSummary, record: StoredEvent): void {
  const markerSummary = dropSummaryFromMarker(record);
  if (markerSummary) {
    mergeDropSummary(summary, markerSummary);
    return;
  }
  mergeDropSummary(summary, {
    count: 1,
    bytes: record.bytes,
    oldestOccurredAt: record.event.occurredAt,
    newestOccurredAt: record.event.occurredAt,
  });
}

function coalesceDropMarkers(records: StoredEvent[], summary: DropSummary): StoredEvent[] {
  return records.filter((record) => {
    const markerSummary = dropSummaryFromMarker(record);
    if (!markerSummary) return true;
    mergeDropSummary(summary, markerSummary);
    return false;
  });
}

function overLimit(records: StoredEvent[], maxEvents: number, maxBytes: number): boolean {
  if (records.length > maxEvents) return true;
  return records.reduce((total, record) => total + record.bytes, 0) > maxBytes;
}

/**
 * Pure cap helper.  Records are returned oldest-first and the dropped summary
 * is deterministic, which makes the cap/drop behavior directly testable in
 * Node without a browser or IndexedDB implementation.
 */
export function applyOutboxCaps(
  input: StoredEvent[],
  maxEvents = OUTBOX_MAX_EVENTS,
  maxBytes = OUTBOX_MAX_BYTES,
  preserveLast = false,
): { records: StoredEvent[]; dropped: DropSummary } {
  const records = [...input].sort((a, b) => a.sequence - b.sequence);
  const dropped = emptyDropSummary();
  while (overLimit(records, maxEvents, maxBytes) && records.length > (preserveLast ? 1 : 0)) {
    const [oldest] = records.splice(0, 1);
    if (oldest) addDrop(dropped, oldest);
  }
  return { records, dropped };
}

function requestResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error("IndexedDB request failed"));
  });
}

function transactionDone(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error || new Error("IndexedDB transaction failed"));
    transaction.onabort = () => reject(transaction.error || new Error("IndexedDB transaction aborted"));
  });
}

class IndexedDbStore implements Store {
  private readonly dbName: string;
  private dbPromise: Promise<IDBDatabase>;

  constructor(dbName: string) {
    this.dbName = dbName;
    this.dbPromise = this.open();
  }

  private open(): Promise<IDBDatabase> {
    if (typeof indexedDB === "undefined") return Promise.reject(new Error("IndexedDB unavailable"));
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, DB_VERSION);
      request.onupgradeneeded = () => {
        if (!request.result.objectStoreNames.contains(STORE_NAME)) {
          request.result.createObjectStore(STORE_NAME, { keyPath: "clientEventId" });
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error || new Error("IndexedDB open failed"));
      request.onblocked = () => reject(new Error("IndexedDB open blocked"));
    });
  }

  async get(clientEventId: string): Promise<StoredEvent | undefined> {
    const db = await this.dbPromise;
    const transaction = db.transaction(STORE_NAME, "readonly");
    return requestResult(transaction.objectStore(STORE_NAME).get(clientEventId));
  }

  async list(): Promise<StoredEvent[]> {
    const db = await this.dbPromise;
    const transaction = db.transaction(STORE_NAME, "readonly");
    const values = await requestResult(transaction.objectStore(STORE_NAME).getAll());
    return (values || []).sort((a, b) => a.sequence - b.sequence);
  }

  async replace(records: StoredEvent[]): Promise<void> {
    const db = await this.dbPromise;
    const transaction = db.transaction(STORE_NAME, "readwrite");
    const objectStore = transaction.objectStore(STORE_NAME);
    objectStore.clear();
    records.forEach((record) => objectStore.put(record));
    await transactionDone(transaction);
  }

  async deleteMany(clientEventIds: string[]): Promise<void> {
    if (!clientEventIds.length) return;
    const db = await this.dbPromise;
    const transaction = db.transaction(STORE_NAME, "readwrite");
    const objectStore = transaction.objectStore(STORE_NAME);
    clientEventIds.forEach((clientEventId) => objectStore.delete(clientEventId));
    await transactionDone(transaction);
  }
}

export class MemoryEventStore implements Store {
  private readonly records = new Map<string, StoredEvent>();

  constructor(initialRecords: StoredEvent[] = []) {
    initialRecords.forEach((record) => this.records.set(record.clientEventId, record));
  }

  async get(clientEventId: string): Promise<StoredEvent | undefined> {
    return this.records.get(clientEventId);
  }

  async list(): Promise<StoredEvent[]> {
    return [...this.records.values()].sort((a, b) => a.sequence - b.sequence);
  }

  async replace(records: StoredEvent[]): Promise<void> {
    this.records.clear();
    records.forEach((record) => this.records.set(record.clientEventId, record));
  }

  async deleteMany(clientEventIds: string[]): Promise<void> {
    clientEventIds.forEach((clientEventId) => this.records.delete(clientEventId));
  }
}

export class EventBatchError extends Error {
  readonly status: number;
  readonly retryable: boolean;

  constructor(status: number, message = "Event batch request failed") {
    super(message);
    this.name = "EventBatchError";
    this.status = status;
    this.retryable = status === 429 || (status >= 500 && status <= 599) || status === 0;
  }
}

export class EventOutbox {
  private readonly sender?: EventSender;
  private readonly now: () => Date;
  private readonly maxEvents: number;
  private readonly maxBytes: number;
  private readonly forceMemory: boolean;
  private readonly dbName: string;
  private store: Store | null = null;
  private mode: "idb" | "memory" = "memory";
  private readyPromise: Promise<void>;
  private sequence = 0;
  private writeChain: Promise<void> = Promise.resolve();
  private flushPromise: Promise<void> | null = null;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private retryCount = 0;
  private fallbackCounter = 0;
  private fallbackNotified = false;
  private lastKnownRecords: StoredEvent[] = [];
  private started = false;
  private cleanupListeners: (() => void) | null = null;

  constructor(options: EventOutboxOptions = {}) {
    this.sender = options.sender;
    this.now = options.now || (() => new Date());
    this.maxEvents = options.maxEvents ?? OUTBOX_MAX_EVENTS;
    this.maxBytes = options.maxBytes ?? OUTBOX_MAX_BYTES;
    this.forceMemory = options.forceMemory === true;
    this.dbName = options.dbName || DEFAULT_DB_NAME;
    this.readyPromise = this.initialize();
  }

  get usingMemoryFallback(): boolean {
    return this.mode === "memory";
  }

  get fallbackMode(): "idb" | "memory" {
    return this.mode;
  }

  get fallbackCount(): number {
    return this.fallbackCounter;
  }

  async fallbackStatus(): Promise<{ mode: "idb" | "memory"; count: number }> {
    await this.ready();
    return { mode: this.mode, count: this.fallbackCounter };
  }

  private remember(records: StoredEvent[]): void {
    this.lastKnownRecords = [...records];
  }

  private appendDropMarker(records: StoredEvent[], summary: DropSummary): StoredEvent[] {
    // Existing markers are folded into the new one before applying caps. This
    // keeps loss evidence bounded even when the queue overflows repeatedly.
    const next = coalesceDropMarkers(records, summary);
    const markerEvent: EventInput = {
      clientEventId: createClientEventId(),
      occurredAt: isoNow(this.now),
      eventName: CLIENT_EVENT_NAME.EVENTS_DROPPED,
      result: EVENT_RESULT.SKIPPED,
      payload: {
        droppedCount: summary.count,
        droppedBytes: summary.bytes,
        oldestOccurredAt: summary.oldestOccurredAt,
        newestOccurredAt: summary.newestOccurredAt,
      },
    };
    const marker = normalizeEvent(markerEvent, this.now);
    if (!marker) return next;
    const markerRecord: StoredEvent = {
      clientEventId: marker.clientEventId,
      event: marker,
      bytes: serializedEventBytes(marker),
      sequence: ++this.sequence,
    };
    next.push(markerRecord);
    while (true) {
      markerRecord.event.payload = {
        droppedCount: summary.count,
        droppedBytes: summary.bytes,
        oldestOccurredAt: summary.oldestOccurredAt,
        newestOccurredAt: summary.newestOccurredAt,
      };
      markerRecord.bytes = serializedEventBytes(markerRecord.event);
      const withMarker = applyOutboxCaps(next, this.maxEvents, this.maxBytes, true);
      next.splice(0, next.length, ...withMarker.records);
      if (!withMarker.dropped.count) break;
      mergeDropSummary(summary, withMarker.dropped);
    }
    return next;
  }

  private async recordOversizedDropUnlocked(
    input: EventInput,
    store: Store,
    allowFallback: boolean,
  ): Promise<void> {
    const occurredAt = normalizedOccurredAt(input.occurredAt, this.now);
    const bytes = boundedDropBytes(byteLength({
      ...input,
      clientEventId: typeof input.clientEventId === "string" && input.clientEventId
        ? input.clientEventId
        : PLACEHOLDER_EVENT_ID,
      occurredAt,
    }));
    try {
      const current = await store.list();
      this.remember(current);
      const dropped = {
        count: 1,
        bytes,
        oldestOccurredAt: occurredAt,
        newestOccurredAt: occurredAt,
      } satisfies DropSummary;
      const records = this.appendDropMarker(current, dropped);
      await store.replace(records);
      this.remember(records);
      this.scheduleFlush(0);
    } catch (error) {
      if (allowFallback && this.mode === "idb") {
        this.activateMemoryFallback(this.lastKnownRecords);
        await this.recordOversizedDropUnlocked(input, this.store!, false);
        return;
      }
      throw error;
    }
  }

  private notifyMemoryFallback(): void {
    if (this.fallbackNotified) return;
    this.fallbackNotified = true;
    queueMicrotask(() => {
      void this.enqueue({
        eventName: CLIENT_EVENT_NAME.EVENT_OUTBOX_FALLBACK,
        result: EVENT_RESULT.FAILED,
        payload: { mode: "memory", fallbackCount: this.fallbackCounter },
      }).catch(() => {});
    });
  }

  private activateMemoryFallback(
    records: StoredEvent[] = this.lastKnownRecords,
    notify = true,
  ): MemoryEventStore {
    const store = new MemoryEventStore(records);
    this.store = store;
    this.mode = "memory";
    this.remember(records);
    this.fallbackCounter = Math.min(1_000_000, this.fallbackCounter + 1);
    if (notify) this.notifyMemoryFallback();
    return store;
  }

  private async initialize(): Promise<void> {
    if (this.forceMemory) {
      this.activateMemoryFallback([], false);
      return;
    }
    try {
      const store = new IndexedDbStore(this.dbName);
      const records = await store.list();
      this.store = store;
      this.mode = "idb";
      this.remember(records);
      records.forEach((record) => {
        this.sequence = Math.max(this.sequence, Number(record.sequence) || 0);
      });
    } catch {
      this.activateMemoryFallback();
    }
  }

  private async ready(): Promise<Store> {
    await this.readyPromise;
    if (!this.store) return this.activateMemoryFallback();
    return this.store;
  }

  private runLocked<T>(operation: () => Promise<T>): Promise<T> {
    const previous = this.writeChain;
    let release!: () => void;
    this.writeChain = new Promise<void>((resolve) => {
      release = resolve;
    });
    return previous
      .then(operation, operation)
      .finally(() => release());
  }

  private async enqueueUnlocked(input: EventInput, internal = false, allowFallback = true): Promise<string | null> {
    const store = await this.ready();
    const event = normalizeEvent(input, this.now);
    if (!event) {
      if (!internal && eventInputIsOversized(input, this.now)) {
        await this.recordOversizedDropUnlocked(input, store, allowFallback);
      }
      return null;
    }
    let fallbackRecords: StoredEvent[] | null = null;
    try {
      const existing = await store.get(event.clientEventId);
      if (existing) return event.clientEventId;
      const record: StoredEvent = {
        clientEventId: event.clientEventId,
        event,
        bytes: serializedEventBytes(event),
        sequence: ++this.sequence,
      };
      const current = await store.list();
      this.remember(current);
      const capped = applyOutboxCaps([...current, record], this.maxEvents, this.maxBytes);
      let records = capped.records;
      const dropped = capped.dropped;
      if (dropped.count && !internal) {
        records = this.appendDropMarker(records, dropped);
      }
      fallbackRecords = records;
      await store.replace(records);
      this.remember(records);
      this.scheduleFlush(0);
      return event.clientEventId;
    } catch (error) {
      if (allowFallback && this.mode === "idb") {
        this.activateMemoryFallback(fallbackRecords || this.lastKnownRecords);
        return this.enqueueUnlocked(input, internal, false);
      }
      throw error;
    }
  }

  enqueue(input: EventInput): Promise<string | null> {
    // Every call gets its own id before it enters the queue.  The id is then
    // retained in the stored event for every network retry.
    const prepared = { ...input, clientEventId: input.clientEventId || createClientEventId() };
    return this.runLocked(() => this.enqueueUnlocked(prepared));
  }

  private async listRecordsWithFallback(): Promise<StoredEvent[]> {
    let store = await this.ready();
    try {
      const records = await store.list();
      this.remember(records);
      return records;
    } catch (error) {
      if (this.mode !== "idb") throw error;
      store = this.activateMemoryFallback();
      const records = await store.list();
      this.remember(records);
      return records;
    }
  }

  async listPending(): Promise<ClientEvent[]> {
    const records = await this.runLocked(() => this.listRecordsWithFallback());
    return records.map((record) => record.event);
  }

  start(): () => void {
    if (this.started) return () => this.stop();
    this.started = true;
    const target = typeof window !== "undefined" ? window : null;
    const onOnline = () => this.flush();
    const onVisible = () => {
      if (typeof document === "undefined" || document.visibilityState === "visible") this.flush();
    };
    target?.addEventListener("online", onOnline);
    target?.addEventListener("pageshow", onVisible);
    typeof document !== "undefined" && document.addEventListener("visibilitychange", onVisible);
    this.cleanupListeners = () => {
      target?.removeEventListener("online", onOnline);
      target?.removeEventListener("pageshow", onVisible);
      typeof document !== "undefined" && document.removeEventListener("visibilitychange", onVisible);
      if (this.retryTimer) clearTimeout(this.retryTimer);
      this.retryTimer = null;
      this.started = false;
    };
    this.flush();
    return () => this.stop();
  }

  stop(): void {
    this.cleanupListeners?.();
    this.cleanupListeners = null;
  }

  private scheduleFlush(delayMs: number): void {
    if (!this.sender || !this.started && delayMs !== 0) return;
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      this.flush();
    }, Math.max(0, delayMs));
  }

  private scheduleRetry(): void {
    const base = Math.min(MAX_RETRY_DELAY_MS, 1_000 * 2 ** Math.min(this.retryCount, 6));
    this.retryCount += 1;
    const jitter = 0.8 + Math.random() * 0.4;
    this.scheduleFlush(Math.round(base * jitter));
  }

  private async dropBatch(batch: StoredEvent[]): Promise<boolean> {
    const batchIds = new Set(batch.map((record) => record.clientEventId));
    return this.runLocked(async () => {
      const persist = async (activeStore: Store): Promise<boolean> => {
        const current = await activeStore.list();
        this.remember(current);
        const dropped = emptyDropSummary();
        const retained = current.filter((record) => {
          if (!batchIds.has(record.clientEventId)) return true;
          addDrop(dropped, record);
          return false;
        });
        if (!dropped.count) return false;
        const hasRetainedEvents = retained.some(
          (record) => record.event.eventName !== CLIENT_EVENT_NAME.EVENTS_DROPPED,
        );
        const records = this.appendDropMarker(retained, dropped);
        await activeStore.replace(records);
        this.remember(records);
        return hasRetainedEvents;
      };

      let activeStore = await this.ready();
      try {
        return await persist(activeStore);
      } catch (error) {
        if (this.mode !== "idb") throw error;
        activeStore = this.activateMemoryFallback();
        return persist(activeStore);
      }
    });
  }

  async flush(): Promise<void> {
    if (!this.sender || this.flushPromise) return this.flushPromise || Promise.resolve();
    this.flushPromise = this.flushInternal().finally(() => {
      this.flushPromise = null;
    });
    return this.flushPromise;
  }

  private async flushInternal(): Promise<void> {
    const records = await this.runLocked(() => this.listRecordsWithFallback());
    if (!records.length) {
      this.retryCount = 0;
      return;
    }
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      return;
    }
    const batch = records.slice(0, EVENT_BATCH_SIZE);
    try {
      const response = await this.sender!(batch.map((record) => record.event));
      const batchIds = new Set(batch.map((record) => record.clientEventId));
      const acknowledged = new Set(
        [...(response?.accepted || []), ...(response?.duplicates || [])].filter((clientEventId) => batchIds.has(clientEventId)),
      );
      if (acknowledged.size) {
        await this.runLocked(async () => {
          let activeStore = await this.ready();
          try {
            await activeStore.deleteMany([...acknowledged]);
            const current = await activeStore.list();
            this.remember(current);
          } catch (error) {
            if (this.mode !== "idb") throw error;
            activeStore = this.activateMemoryFallback();
            await activeStore.deleteMany([...acknowledged]);
            const current = await activeStore.list();
            this.remember(current);
          }
        });
      }
      if (acknowledged.size < batch.length) {
        // A partial ACK is still a retry of the unacknowledged tail. Keep the
        // previous retry count so the exponential backoff is not reset by a
        // server response that made only partial progress.
        this.scheduleRetry();
      } else {
        this.retryCount = 0;
        if ((await this.runLocked(() => this.listRecordsWithFallback())).length) this.scheduleFlush(0);
      }
    } catch (error) {
      // Retryable transport/server failures keep every unacknowledged row. A
      // terminal client rejection is handled below by dropping only this batch.
      if (!(error instanceof EventBatchError) || error.retryable) {
        this.scheduleRetry();
        return;
      }
      // Validation and other 4xx responses cannot be repaired by retrying the
      // same payload. Drop only this batch, preserve later rows, and retain a
      // bounded marker so the loss is observable without head-of-line blocking.
      try {
        const hasRetainedEvents = await this.dropBatch(batch);
        this.retryCount = 0;
        if (hasRetainedEvents) this.scheduleFlush(0);
      } catch {
        // Storage failures remain fail-open for the UI, but the rows must stay
        // durable and get another chance when the store recovers.
        this.scheduleRetry();
      }
    }
  }
}

export type CaptureTarget = {
  record(event: EventInput): void;
  getScreen?: () => string | undefined;
  excludeSelector?: string;
};

function fallbackActionId(element: Element): string {
  const tag = element.tagName.toLowerCase();
  const id = element.getAttribute("id");
  const name = element.getAttribute("name");
  const role = element.getAttribute("role");
  return ["unlabeled", tag, role || "", id || name || ""].filter(Boolean).join(":");
}

function actionElement(target: EventTarget | null): Element | null {
  if (!(target instanceof Element)) return null;
  return target.closest("[data-ui-action],button,a,input,select,textarea,[role='button']");
}

/**
 * Capture discrete controls before React handlers run.  Pointer and click are
 * deduped by element/time; keyboard-only clicks still produce one event.  The
 * mini-game surface is explicitly excluded because its ordered game timeline is
 * submitted as one `ui.game_session` event by the owner component.
 */
export function installEventCapture(options: CaptureTarget): () => void {
  if (typeof document === "undefined") return () => {};
  const exclude = options.excludeSelector || "[data-pitstop]";
  let lastPointerTarget: Element | null = null;
  let lastPointerAction = "";
  let lastPointerAt = 0;
  const emit = (element: Element) => {
    if (element.closest(exclude)) return;
    const bottomNav = element.closest("[data-ui-role='bottom-nav']");
    if (bottomNav) {
      options.record({
        eventName: CLIENT_EVENT_NAME.BOTTOM_NAV_TAPPED,
        result: EVENT_RESULT.STARTED,
        screen: options.getScreen?.(),
        payload: { actionId: element.getAttribute("data-ui-action") || fallbackActionId(element) },
      });
      return;
    }
    const actionId = element.getAttribute("data-ui-action") || fallbackActionId(element);
    options.record({
      eventName: CLIENT_EVENT_NAME.CTA_TAPPED,
      result: EVENT_RESULT.STARTED,
      screen: options.getScreen?.(),
      payload: { actionId },
    });
  };
  const onPointerDown = (event: PointerEvent) => {
    const element = actionElement(event.target);
    if (!element || element.closest(exclude)) return;
    const actionId = element.getAttribute("data-ui-action") || fallbackActionId(element);
    lastPointerTarget = element;
    lastPointerAction = actionId;
    lastPointerAt = Date.now();
    emit(element);
  };
  const onClick = (event: MouseEvent) => {
    const element = actionElement(event.target);
    if (!element || element.closest(exclude)) return;
    const actionId = element.getAttribute("data-ui-action") || fallbackActionId(element);
    if (
      event.detail !== 0
      && element === lastPointerTarget
      && actionId === lastPointerAction
      && Date.now() - lastPointerAt < 800
    ) {
      lastPointerTarget = null;
      lastPointerAction = "";
      lastPointerAt = 0;
      return;
    }
    emit(element);
  };
  const onChange = (event: Event) => {
    const element = actionElement(event.target);
    if (!element || element.closest(exclude)) return;
    const tag = element.tagName.toLowerCase();
    if (tag !== "input" && tag !== "select" && tag !== "textarea") return;
    const field = element as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
    const input = field as HTMLInputElement;
    const isToggle = tag === "input" && ["checkbox", "radio"].includes(input.type);
    // Free text inside a `[data-pii]` form (passport name, PINFL, document
    // fields) must never leave the device inside an analytics payload. The
    // interaction is still recorded - only the typed value is dropped. Toggles
    // carry no personal data and stay verbatim.
    const value = isToggle ? input.checked : element.closest("[data-pii]") ? null : field.value;
    options.record({
      eventName: CLIENT_EVENT_NAME.INPUT_CHANGED,
      result: EVENT_RESULT.RECEIVED,
      screen: options.getScreen?.(),
      payload: {
        actionId: element.getAttribute("data-ui-action") || fallbackActionId(element),
        value,
      },
    });
  };
  document.addEventListener("pointerdown", onPointerDown, true);
  document.addEventListener("click", onClick, true);
  document.addEventListener("change", onChange, true);
  return () => {
    document.removeEventListener("pointerdown", onPointerDown, true);
    document.removeEventListener("click", onClick, true);
    document.removeEventListener("change", onChange, true);
  };
}

export function createEventOutbox(options: EventOutboxOptions = {}): EventOutbox {
  return new EventOutbox(options);
}
